import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export function useEventFloorplans(eventId, event) {
  const store = useStore();
  const router = useRouter();

  const showFloorplanPicker = ref(false);
  const floorplanPickerStep = ref('space');
  const selectedPickerSpace = ref(null);
  const pickerLayouts = ref([]);
  const loadingPickerLayouts = ref(false);

  const eventFloorplans = computed(
    () => store.getters['planningEventFloorplans/getRecords']
  );
  const floorplanUIFlags = computed(
    () => store.getters['planningEventFloorplans/getUIFlags']
  );
  const loadingFloorplans = computed(() => floorplanUIFlags.value.isFetching);
  const creatingFloorplan = computed(() => floorplanUIFlags.value.isCreating);

  const availableSpacesForFloorplan = computed(() => {
    const resources = store.getters['planningResources/getResources'] || [];
    const allSpaces = store.getters['spaces/getSpaces'] || [];
    const usedSpaceIds = new Set(eventFloorplans.value.map(ef => ef.space_id));
    const eventResourceIds = new Set(
      (event.value?.allocated_resources || []).map(ar => ar.resource_id)
    );

    return allSpaces.filter(sp => {
      if (usedSpaceIds.has(sp.id)) return false;
      const matchingResource = resources.find(
        r => r.id === sp.resource_id || r.namespace_id === sp.id
      );
      if (!matchingResource) return false;
      return eventResourceIds.has(matchingResource.id);
    });
  });

  const fetchEventFloorplans = async () => {
    const eid =
      typeof eventId === 'function' ? eventId() : eventId.value || eventId;
    await store.dispatch('planningEventFloorplans/get', eid);
  };

  const openFloorplanPicker = () => {
    showFloorplanPicker.value = true;
    floorplanPickerStep.value = 'space';
    selectedPickerSpace.value = null;
    pickerLayouts.value = [];
  };

  const closeFloorplanPicker = () => {
    showFloorplanPicker.value = false;
    selectedPickerSpace.value = null;
    pickerLayouts.value = [];
  };

  const selectPickerSpace = async space => {
    selectedPickerSpace.value = space;
    floorplanPickerStep.value = 'layout';
    loadingPickerLayouts.value = true;
    try {
      pickerLayouts.value = await store.dispatch(
        'planningEventFloorplans/fetchLayouts',
        space.id
      );
    } catch {
      pickerLayouts.value = [];
    } finally {
      loadingPickerLayouts.value = false;
    }
  };

  const attachFloorplan = async (layoutId = null) => {
    const eid =
      typeof eventId === 'function' ? eventId() : eventId.value || eventId;
    try {
      await store.dispatch('planningEventFloorplans/create', {
        eventId: eid,
        spaceId: selectedPickerSpace.value.id,
        layoutId,
      });
      closeFloorplanPicker();
    } catch {
      // Error shown by API layer
    }
  };

  const deleteEventFloorplan = async floorplanId => {
    const eid =
      typeof eventId === 'function' ? eventId() : eventId.value || eventId;
    try {
      await store.dispatch('planningEventFloorplans/delete', {
        eventId: eid,
        floorplanId,
      });
    } catch {
      // Error shown by API layer
    }
  };

  const openEventFloorplanEditor = floorplanId => {
    const eid =
      typeof eventId === 'function' ? eventId() : eventId.value || eventId;
    router.push({
      name: 'planning_event_floorplan',
      params: { eventId: eid, floorplanId },
    });
  };

  return {
    showFloorplanPicker,
    floorplanPickerStep,
    selectedPickerSpace,
    pickerLayouts,
    loadingPickerLayouts,
    eventFloorplans,
    loadingFloorplans,
    creatingFloorplan,
    availableSpacesForFloorplan,
    fetchEventFloorplans,
    openFloorplanPicker,
    closeFloorplanPicker,
    selectPickerSpace,
    attachFloorplan,
    deleteEventFloorplan,
    openEventFloorplanEditor,
  };
}
